import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { type ExternalLinksContentItem } from '../../data/externallinks';
import { Button, Heading } from '../design-system';

const Wrapper = styled.div<{ $shouldTryToFloat: boolean }>`
  display: flex;
  flex: 1 1 auto;
  position: relative;
  width: 100%;

  ${({ $shouldTryToFloat }) =>
    $shouldTryToFloat &&
    css`
      @media (min-width: 1675px) {
        position: absolute;
        top: 0;
        left: calc(100% + 15px);
        width: 300px;
      `}
    }
`;

const Inside = styled.div`
  --cutout-width: 16ch;
  --cutout-left-x: calc(50% - var(--cutout-width) * 0.5 - 1px);
  --cutout-right-x: calc(50% + var(--cutout-width) * 0.5 + 1px);

  position: relative;
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid ${({ theme }) => rgba(theme.colors.GRAY, 0.15)};
    clip-path: polygon(
      0% 0%,
      var(--cutout-left-x) 0%,
      var(--cutout-left-x) 5px,
      var(--cutout-right-x) 5px,
      var(--cutout-right-x) 0%,
      100% 0%,
      100% 100%,
      0% 100%
    );
  }
`;

const Banner = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  align-items: center;
  position: absolute;
  top: -12px;

  &::before,
  &::after {
    content: '';
    display: flex;
    height: 1px;
    flex: 1 1 auto;
  }
`;

const BannerText = styled(Heading).attrs({ renderAs: 'h6' })`
  display: flex;
  padding: 0 10px;
`;

const ContentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: 30px 25px 20px;
`;

const Item = styled(Button)<{ $shouldTryToFloat: boolean }>`
  && {
    border: none;
    flex: 0 1 calc(100% / 4);
    width: calc(100% / 4);
    display: flex;
    height: auto;
    padding: 10px;

    ${({ $shouldTryToFloat }) =>
      $shouldTryToFloat &&
      css`
        @media (min-width: 1675px) {
          flex: 0 1 100%;
          width: 100%;
        }
      `}

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      flex: 0 1 calc(100% / 3);
      width: calc(100% / 3);
    }

    @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
      flex: 0 1 calc(100% / 2);
      width: calc(100% / 2);
    }

    @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
      flex: 0 1 100%;
      width: 100%;
    }

    > div {
      align-items: flex-start;
      justify-content: flex-start;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-weight: 400;
  min-width: 0;
  white-space: normal;
  text-align: left;
  height: 100%;
`;

const Title = styled.div`
  min-width: 0;
  font-family: ${({ theme }) => theme.fontFamily.SANS};
  color: ${({ theme }) => theme.colors.WHITE};
  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
  margin-bottom: 5px;
  line-height: 20px;
  text-transform: none;
`;

const Source = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.SANS};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  line-height: 20px;
  text-transform: none;
`;

interface ExternalLinksProps {
  className?: string;
  content: ExternalLinksContentItem[];
  label?: string;
  shouldTryToFloat?: boolean;
}

export const ExternalLinks: React.ComponentType<ExternalLinksProps> =
  React.memo(
    ({ content, shouldTryToFloat = false, label = 'Elsewhere', className }) => {
      return (
        <Wrapper $shouldTryToFloat={shouldTryToFloat} className={className}>
          <Inside>
            <Banner>
              <BannerText renderAs="h6">{label}</BannerText>
            </Banner>
            <ContentList>
              {content.map((item, index) => {
                return (
                  <Item
                    key={index}
                    $shouldTryToFloat={shouldTryToFloat}
                    link={item.url}
                    stylez="minimal"
                    text={
                      <Content>
                        <Title>{item.title}</Title>
                        <Source>{item.source}</Source>
                      </Content>
                    }
                  />
                );
              })}
            </ContentList>
          </Inside>
        </Wrapper>
      );
    }
  );
